import { MortgageCalculator } from "@component/Mortgage/Calculator/MortgageCalculator";
import {
	faCalculator as fasCalculator,
	faPhone as fasPhone,
	faPaste as fasPaste,
	faFileSignature as fasFileSignature,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { InfoCard } from "@ui/components/InfoCard";
import { currentYear } from "@utils/current-year";
import { LandingContainer, LandingHowTo, LandingHowToItem, __ } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Accordion, Card, Col, Row } from "react-bootstrap";

/**
 * @Routing
 *
 * @title "CSOK hitel kalkulátor - CSOK+lakáshitel"
 * @description "Ellenőrizd, hogy jogosult vagy-e CSOK támogatásra, és keresd meg mellé a legkedvezőbb CSOK lakáshitelt a Bankmonitor hitelkalkulátorával"
 * @route "/csok/"
 */

export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<MortgageCalculator
					title={__("CSOK-hitel kalkulátor")}
					description={__(
						"Maximum 3%-os kamat és futamidő végéig kiszámítható, fix havi törlesztő. Kalkulálj és válogass a szuperolcsó CSOK-hitelek között!",
					)}
					button={__("CSOK-hitelek összehasonlítása")}
					variant="CSOK"
				/>
				<LandingContainer>
					<InfoCard title="TIPP" variant="retail" className="text-white">
						<Card.Body>
							2024-től érkezik a CSOK Plusz, ami az ország összes településén elérhető lesz a gyereket
							vállaló párok számára. Itt tudhatsz meg{" "}
							<a href="/csok-plusz/?link_type=info-box">minden aktuális információt a CSOK Pluszról,</a>{" "}
							és akár percek alatt ellenőrizheted azt is, hogy jogosultak vagytok-e az új otthonteremtési
							támogatásra.
						</Card.Body>
					</InfoCard>
				</LandingContainer>
				<LandingHowTo title="Így kaphatod meg a CSOK-hitelt">
					<LandingHowToItem title="Kalkuláció" icon={<FontAwesomeIcon icon={fasCalculator} />}>
						Versenyeztesd meg a bankok ajánlatait, és válaszd ki a <strong>számodra legkedvezőbbet</strong>.
					</LandingHowToItem>
					<LandingHowToItem title="Érdeklődés" icon={<FontAwesomeIcon icon={fasPhone} />}>
						Add le érdeklődésed a választott lakáshitelre, és <strong>szakértőnk visszahív</strong>.
					</LandingHowToItem>
					<LandingHowToItem title="Ügyintézés" icon={<FontAwesomeIcon icon={fasPaste} />}>
						Ha kiválasztottad a számodra megfelelő bankot, <strong>segítünk az ügyintézésben is</strong>.
					</LandingHowToItem>
					<LandingHowToItem title="Szerződéskötés" icon={<FontAwesomeIcon icon={fasFileSignature} />}>
						A szerződést minden esetben szakértőnk segítségével és az általad kiválasztott bankkal kötöd
					</LandingHowToItem>
				</LandingHowTo>
				{this.renderFaq()}
				<LandingBottom tags="lakashitel" />
			</>
		);
	}

	private renderFaq(): ReactNode {
		return (
			<LandingContainer>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
							CSOK-hitel jellemzői {currentYear(true)}
						</h2>
						<Accordion>
							<LandingAccordion title="Melyek a csok-hitel feltételei?">
								<p>
									Ahhoz, hogy megkaphasd a maximum 3%-os kamatozású, szuperolcsó CSOK-hitelt, három
									dolognak mindenképpen meg kell felelned.
								</p>
								<ol>
									<li>
										Először is meg kell felelned a CSOK (családi otthonteremtési kedvezmény)
										jogosultsági feltételeinek! Azt, hogy jogosult vagy-e a CSOK-ra, azt a{" "}
										<a href="/csok-feltetelei/?link_type=landing-faq">
											Bankmonitor CSOK kalkulátorával
										</a>{" "}
										gyorsan ellenőrizheted.
									</li>
									<li>
										Legalább két gyermek után ki is kell használni a vissza nem térítendő
										lakástámogatást.
									</li>
									<li>
										Mivel hitelről van szó, hitelképesnek kell lenned, vagyis rendelkezned kell
										megfelelő nagyságú, igazolt jövedelemmel, ami a megvásárolni vagy felépíteni
										kívánt ingatlannak elegendő fedezetet nyújt, valamint elő kell tudnod teremteni
										a CSOK-hitelhez szükséges önerőt. Az önerő lehet akár saját megtakarítás, de
										meghatározott feltételekkel a{" "}
										<a href="/babavaro-hitel/?link_type=landing-faq">Babaváró támogatás</a> és a
										vissza nem térítendő{" "}
										<a href="/jobb-tudni/csok/?link_type=landing-faq#csaladi-otthonteremtesi-kedvezmeny-csok">
											CSOK támogatás
										</a>{" "}
										is felhasználható erre a célra.{" "}
										<a href="/mediatar/cikk/csok-onerokent-hasznalva-akar-35-millio-ft-ot-is-jelenthet/?link_type=landing-faq">
											A CSOK önerőként történő elfogadásáról itt olvashatsz részletesen.
										</a>
									</li>
								</ol>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion
								title={`Milyen tb-jogviszony kell a CSOK-hoz, ezzel pedig a CSOK-hitelhez ${currentYear(
									true,
								)}?`}
							>
								<p>
									Már volt róla szó, hogy a CSOK-hitel feltétele a CSOK jogosultság, ezért ha
									szeretnéd kihasználni a kedvező kamat lehetőségét, először a CSOK feltételeit kell
									teljesítened. Itt pedig nagyon fontos a megfelelő tb-jogviszony megléte.
								</p>
								<p>
									A <a href="/csok-feltetelei/?link_type=landing-faq">CSOK</a> rendelet különbséget
									tesz aszerint, hogy új vagy használt lakásra, illetve 2 vagy 3 gyermekre igényli
									valaki a támogatást. Amennyiben újépítésű ingatlant vásárolnál vagy építenél, és 2
									gyermekre szeretnéd igénybe venni a CSOK-ot, akkor legalább 180 nap tb-jogviszony az
									elvárás. 3 gyermek, azaz 10 millió forint támogatási összeg esetén ugyanakkor már
									legalább 2 évet kell igazolni.
								</p>
								<p>
									Használt lakás vásárlásakor, függetlenül a meglévő gyermekek számától, 180 nap az
									elvárt minimum. Használt lakás vásárlásakor a közmunka is beleszámít a
									tb-jogviszonyba, ám újépítésű lakás vásárlásakor ezzel nem lehet számolni.
								</p>
								<p>
									A jelenleg külföldön élőknek lényeges, hogy a CSOK külföldi tb-jogviszony
									igazolásával is elérhető, ha nyilatkoznak, miszerint 6 hónapon belül magyarországi
									tb-jogviszonnyal rendelkeznek majd.
								</p>
								<p>
									A <a href="/csok-feltetelei/?link_type=landing-faq">Falusi CSOK</a> a CSOK speciális
									típusa, ami preferált kistelepülésen lévő lakás esetében vásárlásra és egyidejűleg
									bővítésre és/vagy korszerűsítésre; meglévő lakás bővítésére/korszerűsítésére (vagy
									akár egyszerre mindkettőre) fordítható. A Falusi CSOK esetében 1 év a minimálisan
									elvárt tb-jogviszony, ám 10 millió Ft-os támogatási összeg igénylésekor 2 év az
									elvárás. Itt is elfogadott a külföldi tb-jogviszony akkor, ha az igénylőnek fél éven
									belül lesz magyarországi jogosultsága.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Ha jogosult vagyok a CSOK-ra, akkor a CSOK lakáshitelt is megkapom?">
								<p>
									Sokak fejében összekeveredik a vissza nem térítendő CSOK (családok otthonteremtési
									kedvezménye) támogatás és a{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">CSOK-hitel</a> fogalma.
									Fontos különbség azonban, hogy míg az előbbi minden olyan gyermekét nevelő szülőnek
									jár, aki megfelel a támogatási feltételeknek, addig az utóbbi – ahogy azt korábban
									már írtuk – egy nagyon kedvezményes, legfeljebb 3%-os fix kamatozású banki hitel.
								</p>
								<p>
									Ez azt jelenti, hogy a CSOK lakáshitel esetében szükség van banki{" "}
									<a href="/jobb-tudni/lakashitel/?link_type=landing-faq#hitelbiralat">
										hitelbírálatra
									</a>{" "}
									is, amikor ellenőrzik, hogy a megvásárolni kívánt ingatlan fedezetként bevonható-e,
									illetve az igénylők jövedelme elbírja-e a kért hitelösszeg havi törlesztőjét.
								</p>
								<p>
									Emiatt egyáltalán nem biztos, hogy azért, mert valaki jogosult a meglévő vagy
									vállalt gyermekei után CSOK támogatásra, automatikusan CSOK-hitelt is kap majd a
									banktól. Pedig a{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">CSOK-hitel</a> jelentős
									pénzügyi előnyt jelent, hiszen az egész futamidő alatt fix 3%-os támogatott kamat
									lényegesen kedvezőbb a piaci hiteleknél.{" "}
									<strong>
										Ez egy 10 millió Ft-os, 20 éves futamidejű lakáshitel esetében több mint 7
										millió Ft pénzügyi előnyt jelent teljes visszafizetésben a legkedvezőbb piaci
										lakáshitelhez képest!
									</strong>
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title={`Mennyi CSOK-hitelt kaphatok ${currentYear(true)}?`}>
								<p>
									A felvehető CSOK-hitel összege független attól, hogy újépítésű vagy használt
									ingatlant vásárolunk, kizárólag a meglévő vagy vállalt gyerekszám határozza meg a
									lehetőségeket. 2 gyermeknél 10 millió forint, 3 vagy több gyermeknél pedig 15 millió
									forint a megigényelhető CSOK lakáshitel felső határa.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="A CSOK-hitel igénylésének lépései">
								<p>
									<ol>
										<li>
											Ellenőrizd, hogy megfelelsz-e a{" "}
											<a href="/csok-feltetelei/?link_type=landing-faq">CSOK feltételeinek</a>! Ha
											nem, akkor{" "}
											<a href="/lakashitel-kalkulator/?link_type=landing-faq">
												ezen lakáshitelek közül választhatsz!
											</a>
										</li>
										<li>
											Amennyiben jogosult vagy CSOK-ra, akkor először meg kell keresned a számodra
											legjobb hitelajánlatot, amiben segítségedre lehet a{" "}
											<a
												rel="nofollow"
												href="/lakashitel-kalkulator/?loanAmount=10000000&income=250000&realEstateValue=20000000&numberOfChildren=3&csok=true&interestPeriods[]=FIVE_YEARS&socialDiscount=true&start=0&link_type=landing-faq"
											>
												Bankmonitor részletes lakáshitel kalkulátora.
											</a>
										</li>
										<li>
											Be kell nyújtanod CSOK-igénylésed a kiválasztott pénzintézethez, ha pedig
											mindent rendben találnak, elutalják a támogatást.
										</li>
										<li>
											Amennyiben CSOK-hitelt is szeretnél, akkor hitelkérelmet kell benyújtanod a
											kiválasztott pénzintézethez.
										</li>
										<li>
											A hitelintézet elbírálja a hitelkérelmedet, ha itt zöld lámpát kapsz, akkor
											a belső szabályzatuknak megfelelően folyósítják az összeget.
										</li>
									</ol>
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Mennyivel olcsóbb a CSOK-hitel a piaci lakáshiteleknél?">
								<p>
									A Bankmonitor CSOK-hitel kalkulátor szerint – 20 éves futamidő, 3 gyermek és 300
									ezer forint igazolt nettó jövedelem esetén – 15 millió forintos kölcsön esetén 83
									200 Ft körüli havi törlesztőre számíthatsz. Piaci lakáshitelben egy hasonló, végig
									fix kamatú lakáshitel havi törlesztője ugyanakkor 132 000 és 150 000 Ft között van,
									ami 48 800 – 66 800 Ft különbséget jelent havonta!
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen kamatkockázata van egy CSOK-hitelnek?">
								<p>
									A CSOK-hitel fontos előnye az alacsony, maximum 3%-os kamat mellett a futamidő
									végéig kiszámítható, fix havi törlesztő.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen futamidejű lehet a CSOK-hitel?">
								<p>
									A CSOK-hitel futamideje maximum 25 év lehet, de az igénylő ennél rövidebb
									visszafizetési időt is meghatározhat. A CSOK-hitelek esetében – a{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">lakáshitelekhez</a> és a{" "}
									<a href="/szemelyi-kolcson/?link_type=landing-faq">személyi kölcsönökhöz</a>{" "}
									hasonlóan – természetesen lehetőség van előtörleszteni, amikor vagy a havi
									törlesztők nagysága, vagy a hátralévő futamidő csökkenthető le. A teljes
									visszafizetendő összeg nagyságát illetően{" "}
									<a href="/mediatar/cikk/elotorlesztene-most-a-lakashitelet-megmutatjuk-mennyit-nyerhet-vele/?link_type=landing-faq">
										a futamidő lerövidítése jelent nagyobb pénzügyi előnyt
									</a>
									. Ám, hogy éppen melyik a jobb döntés, az természetesen mindig az aktuális
									élethelyzet kérdése.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen költséggel jár egy CSOK-hitel elő- vagy végtörlesztése?">
								<p>
									A hitelek, így a CSOK-hitelek előtörlesztése és végtörlesztése is költséggel jár, a
									bankok általában a befizetett összeg 1,5-2%-át számítják fel díjként.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion
								title={`Mi alapján válasszunk szuperolcsó CSOK-hitelt ${currentYear(true)}?`}
							>
								<p>
									A 9 hazai bank ajánlatát összehasonlító{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">
										Bankmonitor Lakáshitel Kalkulátor
									</a>{" "}
									szerint a piacon elérhető 10 legkedvezőbb CSOK-hitel{" "}
									<a href="/mediatar/cikk/az-emberek-nagy-reszenek-halvany-goze-sincs-a-kamatrol-es-a-thm-rol/?link_type=landing-faq">
										THM értéke
									</a>{" "}
									3,04%-3,18% között van. Mivel a kamat azonos nagyságú, ezért az eltérés a kapcsolódó
									díjakban keresendő. Néhány tízezer forint különbség lehet például az egyszeri,
									induló költségekben. Ahogy fentebb írtuk, az elő- és végtörlesztési díjak között is
									lehet differencia, ami ugyancsak több tízezer forintos pénzügyi előnyt jelenthet, ha
									okosan választasz.
								</p>
								<p>
									Amennyiben azonos vagy nagyon hasonló kondíciókkal nyújtanak a bankok egy kölcsönt,
									akkor felértékelődik a kapcsolódó szolgáltatások minősége és díja. Sok esetben
									például szükséges folyószámlát vezetned annál a banknál, amelynél a hitelt
									felveszed. A bankszámlák díjaival is érdemes számolni, hiszen a hitel főbb
									paraméterei azonosak, ugyanakkor a számlaköltségek között akár több ezer forint
									eltérés is lehet havonta. A{" "}
									<a href="/bankszamla/?link_type=landing-faq">
										Bankmonitor Lakossági Bankszámla Kalkulátora
									</a>{" "}
									segíthet kiválasztani a legkedvezőbb folyószámlát, így a hitellel együtt tudod
									minimalizálni a költségeidet.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Egy CSOK-hitel is csökkenthető a gyermekek után járó hitelelengedéssel?">
								<p>
									Sokan már tudják, hogy 2019 óta a második gyermek után 1 millió forinttal, a
									harmadik után pedig 4 millió forinttal csökkenthető egy már meglévő lakáshitel
									tőketartozása. De vajon a szuper kedvezményes, fix 3%-os kamatú CSOK-hiteleknél is
									élhetünk a lehetőséggel? Jó hír, hogy CSOK-hitelre is érvényes a hitelelengedés,
									ugyanis ez minden esetben lakáscélú jelzáloghitel. Ehhez az kell, hogy a gyermek
									születésekor már meglegyen a lakáshitel.{" "}
									<a href="/mediatar/cikk/egy-csok-hitel-is-csokkentheto-a-gyermekek-utan-jaro-1-es-4-millio-ft-os-hitelelengedessel/?link_type=landing-faq">
										További részleteket itt olvashatsz a hitelelengedésről.
									</a>
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Elbukhatod a CSOK-hitelt, ha a vételár túl nagy részét fizeted készpénzben!">
								<p>
									A CSOK-nak – és ezáltal a CSOK-hitelnek – van egy olyan feltétele, miszerint a
									vételárnak maximum 10%-át lehet készpénzben átadni az eladónak. Például egy 30
									millió Ft-os ingatlan esetében maximum 3 millió forintot lehet készpénzben
									kifizetni. Önmagában ez nem tűnik teljesíthetetlennek, azonban sokan csak az
									adásvételi megkötése és a foglaló megfizetése után szembesülnek ezzel a szabállyal,
									ez pedig megnehezítheti a CSOK igénylésüket.
								</p>
								<p>
									Természetesen van megoldás a problémára. Először is módosítani szükséges az
									adásvételi szerződést: az egyes részletek nagyságát és azok megfizetési módját úgy
									kell átalakítani, hogy a készpénzben átadott összeg ne haladja meg a vételár 10%-át.
									Amennyiben már ki lett fizetve ennél nagyobb összeg, akkor azt vissza kell kapnod,
									és az adásvételiben rögzített más formában átadni a ház jelenlegi tulajdonosainak.
									Ez a másik forma pedig lehet átutalás vagy bankszámlára történő befizetés.
								</p>
								<p>
									Sajnos az adásvételi módosításáért fizetned kell az ügyvédnek, emellett előfordulhat
									az is, hogy az eladó a nem tervezett extra költségeit is megfizetteti veled.
									(Például a számlájáról a készpénzfelvétel díját átterhelheti rád.) Ráadásul egy
									ilyen „apró” hiba miatt elhúzódhat az ügyintézési idő, és később költözhetsz be az
									új otthonodba.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="A túlárazott ingatlan miatt is elbukhatjuk a CSOK-hitelt">
								<p>
									A CSOK rendelet szerint amennyiben a vételár és a bank által meghatározott forgalmi
									érték között 20%-nál nagyobb a különbség, akkor automatikusan elutasítják a
									CSOK-igénylést. Amennyiben pedig valaki nem felel meg a CSOK feltételeinek, akkor
									nem csak a vissza nem térítendő támogatást, hanem a CSOK-hitelt (vagy éppen 2019.
									július 1-től elérhető a Falusi CSOK-ot) is elbukja.
								</p>
								<p>
									Ekkora különbség normál esetben nem nagyon szokott előfordulni, ám ha drágán vásárol
									valaki, a bank pedig alacsonyra lövi be a forgalmi értéket, akkor összejöhet.
								</p>
								<p>
									Jó hír ugyanakkor, hogy amennyiben egy adott bank értékbecslése és a vételár között
									20% feletti a különbség, meg lehet próbálkozni más pénzintézettel, hátha ott
									magasabb értéket állapítanak meg. Ha ott szerencsével jársz, akkor jogosult lehetsz
									a CSOK-ra, ezzel együtt pedig a CSOK-hitelre is.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}
}
